import React from 'react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { CaretRight, CaretDown, FileText, DotsThree, Plus, Trash } from '@phosphor-icons/react';

// Flatten a page tree into rows annotated with depth + visibility, respecting the
// `expanded` set. Iterative — avoids JSX self-recursion that some Babel versions
// stack-overflow on.
export function flattenTree(tree, expanded) {
  const rows = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      const hasChildren = n.children && n.children.length > 0;
      rows.push({ node: n, depth, hasChildren });
      if (hasChildren && expanded.has(n.page_id)) walk(n.children, depth + 1);
    }
  };
  walk(tree, 0);
  return rows;
}

// Stateless single-row component — no recursion inside JSX.
export default function PageNodeRow({ node, depth, hasChildren, expanded, activeId, onSelect, onCreateChild, onDelete, onToggle, canEdit }) {
  const open = expanded.has(node.page_id);
  const active = activeId === node.page_id;
  return (
    <div data-testid={`page-tree-${node.page_id}`}>
      <div
        className={`group flex items-center gap-1 px-1.5 py-1 rounded-md cursor-pointer transition-colors ${
          active ? 'bg-blue-50 text-blue-700' : 'text-zinc-700 hover:bg-zinc-100/80'
        }`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        onClick={() => onSelect(node.page_id)}
      >
        <button
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.page_id); }}
          className={`w-4 h-4 flex items-center justify-center rounded hover:bg-zinc-200 ${hasChildren ? '' : 'opacity-0 pointer-events-none'}`}
          data-testid={`page-toggle-${node.page_id}`}
        >
          {hasChildren && (open ? <CaretDown size={10} /> : <CaretRight size={10} />)}
        </button>
        <FileText size={12} className={active ? 'text-blue-600' : 'text-zinc-400'} weight={active ? 'fill' : 'regular'} />
        <span className="text-[12px] truncate flex-1 font-medium">{node.title || 'Untitled'}</span>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button
                className="w-5 h-5 flex items-center justify-center rounded text-zinc-400 hover:bg-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`page-menu-${node.page_id}`}
              >
                <DotsThree size={12} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => onCreateChild(node.page_id)} data-testid={`page-add-child-${node.page_id}`}>
                <Plus size={12} className="mr-2" /> Add subpage
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDelete(node)} className="text-red-600 focus:text-red-700" data-testid={`page-delete-${node.page_id}`}>
                <Trash size={12} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
